import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";

/**
 * Derive the ordered list of field ids that belong to a saved view by reading
 * `savedViewMembership` off each field declaration.
 *
 * Each company field declares which saved views it appears in. This helper
 * inverts that mapping so saved-view definitions can be the projection rather
 * than the source of truth — making field declarations self-contained.
 *
 * Iteration order over `fields` (the object property order) determines the
 * order of the returned ids. Field files are spread into the `fields` map in
 * a fixed order in `EnhancedCompaniesConfig.ts`, so the result is stable.
 *
 * Generic `TId` lets callers keep a literal field-id union (e.g. `CompanyFieldId`).
 */
export function collectFieldsForSavedView<TId extends string>(
  viewId: string,
  fields: Readonly<Record<TId, SimpleFieldConfig>>
): TId[] {
  const ids: TId[] = [];
  for (const key of Object.keys(fields) as TId[]) {
    const membership = fields[key].savedViewMembership;
    if (membership && membership.includes(viewId)) {
      ids.push(key);
    }
  }
  return ids;
}
